import { AsyncPipe } from '@angular/common';
import { Component, computed, effect, forwardRef, inject, input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { GenericDropdownComponent } from '@zayan-erp/shared';
import { Store } from '@ngrx/store';
import { ADDRESS_TYPE_OPTIONS, AddressFieldConfig } from '@zayan-erp/home-building';
import { DropdownData, InputFieldComponent } from '@zayan-erp/shared';
import { selectCities } from '@zayan-erp/stores';

@Component({
  selector: 'app-addresses',
  standalone: true,
  templateUrl: './addresses.component.html',
  imports: [GenericDropdownComponent, ReactiveFormsModule, FormsModule, AsyncPipe, InputFieldComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressesComponent),
      multi: true
    }
  ]
})
export class AddressesComponent implements ControlValueAccessor {
  #store = inject(Store);
  submitted = input<boolean>(false);
  fieldConfig = input<AddressFieldConfig[]>([]);

  addressForm = new FormGroup({
    addressLine1: new FormControl(''),
    addressLine2: new FormControl(''),
    suite: new FormControl(''),
    postalCode: new FormControl(''),
    cityId: new FormControl(''),
    addressType: new FormControl('')
  });

  fieldConfigs = computed(() => this.fieldConfig());
  visibleFields = computed(() => this.fieldConfigs().filter(config => config.visible));

  cities$ = this.#store.select(selectCities);
  addressTypes: DropdownData[] = ADDRESS_TYPE_OPTIONS.map(option => ({
    id: option.value,
    firstLabel: option.label,
    secondLabel: null
  }));

  private onChange = (value: any) => {};
  private onTouched = () => {};
  disabled = false;

  constructor() {
    this.addressForm.valueChanges.subscribe(value => {
      this.onChange(value);
    });

    effect(() => {
      this.fieldConfig();
      this.updateValidators();
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.addressForm.patchValue(value, { emitEvent: false });
    } else {
      this.addressForm.reset();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.addressForm.disable();
    } else {
      this.addressForm.enable();
    }
  }

  private updateValidators(): void {
    const configs = this.fieldConfig();

    configs.forEach(config => {
      const control = this.addressForm.get(config.field);
      if (control && control instanceof FormControl) {
        if (config.required && config.visible) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
  }

  onFieldTouched(): void {
    this.onTouched();
  }

  getFieldConfig(fieldName: string) {
    return this.fieldConfigs().find(config => config.field === fieldName);
  }

  getColumnClass(colSpan: number): string {
    return `col-${colSpan}`;
  }

  isFieldRequired(fieldName: string): boolean {
    const config = this.getFieldConfig(fieldName);
    return config?.required || false;
  }

  getFieldType(fieldName: string): string {
    const types: { [key: string]: string } = {
      addressLine1: 'textarea',
      addressLine2: 'text',
      suite: 'text',
      postalCode: 'text',
      cityId: 'dropdown',
      addressType: 'dropdown'
    };
    return types[fieldName] || 'text';
  }

  getFormValue() {
    return this.addressForm.value;
  }

  isValid(): boolean {
    return this.addressForm.valid;
  }
}
